import { apiClient } from '@/shared/utils/api';
import { BACKEND_CONFIG } from '@/config/backendConfig';

export interface TimeLog {
  time_log_id: string;
  staff_id: string;
  staff_name: string;
  staff_email: string;
  clocked_in_at: string;
  clocked_out_at: string | null;
  status: 'clocked_in' | 'clocked_out';
  is_on_break: boolean;
  duration_hours: number | null;
  total_break_minutes: number;
  net_work_hours: number | null;
  notes: string | null;
  is_approved: boolean;
  approved_by: string | null;
}

export interface StaffSummary {
  staff_id: string;
  staff_name: string;
  email: string;
  is_clocked_in: boolean;
  current_shift_start: string | null;
  total_hours: number;
  shift_count: number;
}

export interface TimeLogsResponse {
  count: number;
  total: number;
  offset: number;
  limit: number;
  time_logs: TimeLog[];
}

export interface StaffSummaryResponse {
  count: number;
  staff_summaries: StaffSummary[];
}

export interface TimeLogFilters {
  staff_id?: string;
  start_date?: string;
  end_date?: string;
  status?: 'clocked_in' | 'clocked_out';
  limit?: number;
  offset?: number;
}

class TimeTrackingService {
  async getAllStaffTimeLogs(filters: TimeLogFilters = {}): Promise<TimeLogsResponse> {
    const params = new URLSearchParams();

    if (filters.staff_id) params.append('staff_id', filters.staff_id);
    if (filters.start_date) params.append('start_date', filters.start_date);
    if (filters.end_date) params.append('end_date', filters.end_date);
    if (filters.status) params.append('status', filters.status);
    if (filters.limit) params.append('limit', filters.limit.toString());
    if (filters.offset) params.append('offset', filters.offset.toString());

    const response = await apiClient.get(
      `/time-log/admin/all-logs?${params.toString()}`
    );
    return response.data;
  }

  async getStaffSummary(startDate?: string, endDate?: string): Promise<StaffSummaryResponse> {
    const params = new URLSearchParams();

    if (startDate) params.append('start_date', startDate);
    if (endDate) params.append('end_date', endDate);

    const response = await apiClient.get(
      `/time-log/admin/staff-summary?${params.toString()}`
    );
    return response.data;
  }

  async getMyTimeLogs(startDate?: string, endDate?: string, limit: number = 30): Promise<{ count: number; time_logs: Omit<TimeLog, 'staff_id' | 'staff_name' | 'staff_email'>[] }> {
    const params = new URLSearchParams();

    if (startDate) params.append('start_date', startDate);
    if (endDate) params.append('end_date', endDate);
    params.append('limit', limit.toString());

    const endpoint = BACKEND_CONFIG.endpoints.timeLogHistory(BACKEND_CONFIG.isFastAPI);
    const response = await apiClient.get(
      `${endpoint}?${params.toString()}`
    );
    return response.data;
  }

  // Clock in/out methods (work with both backends)
  async clockIn(notes?: string): Promise<any> {
    const endpoint = BACKEND_CONFIG.endpoints.clockIn(BACKEND_CONFIG.isFastAPI);
    const response = await apiClient.post(endpoint, { notes });
    return response.data;
  }

  async clockOut(notes?: string): Promise<any> {
    const endpoint = BACKEND_CONFIG.endpoints.clockOut(BACKEND_CONFIG.isFastAPI);
    const response = await apiClient.post(endpoint, { notes });
    return response.data;
  }

  async getCurrentSession(): Promise<any> {
    const endpoint = BACKEND_CONFIG.endpoints.currentSession(BACKEND_CONFIG.isFastAPI);
    const response = await apiClient.get(endpoint);
    return response.data;
  }

  async getClockStatus(): Promise<{
    is_clocked_in: boolean;
    clock_in_time: string | null;
    duration_hours: number | null;
    time_log_id: string | null;
  }> {
    const endpoint = BACKEND_CONFIG.endpoints.clockStatus(BACKEND_CONFIG.isFastAPI);
    const response = await apiClient.get(endpoint);
    return response.data;
  }

  async startBreak(): Promise<any> {
    const endpoint = BACKEND_CONFIG.endpoints.breakStart(BACKEND_CONFIG.isFastAPI);
    const response = await apiClient.post(endpoint, {});
    return response.data;
  }

  async endBreak(): Promise<any> {
    const endpoint = BACKEND_CONFIG.endpoints.breakEnd(BACKEND_CONFIG.isFastAPI);
    const response = await apiClient.post(endpoint, {});
    return response.data;
  }
}

export const timeTrackingService = new TimeTrackingService();
